import { Component, OnInit } from '@angular/core';
import { SettingsService } from '../../../services/settings.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DataService } from 'src/app/services/data.service';
import { TableService } from 'src/app/services/table.service';
import { LoggerService } from 'src/app/common/services/logger.service';
import { MenuService } from 'src/app/routes/menu/menu.service';

@Component({
  selector: 'app-dynamic-entities',
  templateUrl: './dynamic-entities.component.html',
  styleUrls: ['./dynamic-entities.component.sass'],
  providers: [ConfirmationService, MessageService]
})
export class DynamicEntitiesComponent implements OnInit {
  menuItems: any[] = [];
  selectedItems: any[] = [];
  showJsonDialog: boolean = false;
  selectedItemJson: any = null;
  selectedItemLabel: string = '';
  editableJson: string = '';
  toasterLife: number = 3000;
  sideMenuItems: any[] = [];
  editingIndex: number | null = null;
  isAddSectionModalOpen: boolean = false;
  newSectionName: string = "";
  currentMenuType: 'main' | 'side' | null = null;

  /** Side menu item form */
  sideMenuItemForm: {
    label: string;
    type: 'single' | 'collapsible';
    icon: string;
    route: string;
    action: string;
    permission: string;
    visible: boolean;
    subMenu: Array<{ label: string; route: string; action: string; permission: string; }>;
  } = this.createEmptySideMenuItemForm();
  editingSideMenuItemIndex: number | null = null;
  isAddSideMenuItemModalOpen: boolean = false;

  /** Main menu section form (replaces raw JSON for main menu) */
  editingSectionIndex: number | null = null;
  sectionForm: {
    label: string;
    items: Array<{
      label: string;
      url: string;
      icon: string;
      subItems: Array<{
        label: string;
        url: string;
        icon: string;
        target: string;
      }>;
    }>;
  } = this.createEmptySectionForm();
  

  constructor(
    private settingsService: SettingsService,
    private messageService: MessageService,
    private dataService: DataService,
    private tableService: TableService,
    private loggerService: LoggerService,
    private confirmationService: ConfirmationService,
    private menuService: MenuService
  ) {}

  private createEmptySectionForm(): DynamicEntitiesComponent['sectionForm'] {
    return {
      label: '',
      items: []
    };
  }

  private createEmptyItem(): DynamicEntitiesComponent['sectionForm']['items'][0] {
    return { label: '', url: '', icon: '', subItems: [] };
  }

  private createEmptySubItem(): DynamicEntitiesComponent['sectionForm']['items'][0]['subItems'][0] {
    return { label: '', url: '', icon: 'pi pi-fw pi-plus', target: '_blank' };
  }

  /** Build section JSON from form (for main menu) */
  private sectionFormToSection(): any {
    return {
      label: this.sectionForm.label.trim(),
      items: this.sectionForm.items.map(item => ({
        label: item.label.trim(),
        url: item.url.trim(),
        icon: item.icon?.trim() || undefined,
        items: item.subItems.map(sub => ({
          label: sub.label.trim(),
          url: sub.url.trim(),
          icon: sub.icon?.trim() || 'pi pi-fw pi-plus',
          target: sub.target?.trim() || '_blank'
        }))
      }))
    };
  }

  /** Load form from existing section (for editing main menu) */
  private loadFormFromSection(section: any): void {
    const items = Array.isArray(section.items) ? section.items : (section.items ? Object.values(section.items) : []);
    this.sectionForm = {
      label: section.label || '',
      items: items.map((item: any) => {
        const subItems = Array.isArray(item.items) ? item.items : (item.items ? Object.values(item.items) : []);
        return {
          label: item.label || '',
          url: item.url || '',
          icon: item.icon || '',
          subItems: subItems.map((sub: any) => ({
            label: sub.label || '',
            url: sub.url || '',
            icon: sub.icon || 'pi pi-fw pi-plus',
            target: sub.target || '_blank'
          }))
        };
      })
    };
  }

  addFormItem(): void {
    this.sectionForm.items.push(this.createEmptyItem());
  }

  removeFormItem(index: number): void {
    this.sectionForm.items.splice(index, 1);
  }

  addFormSubItem(itemIndex: number): void {
    this.sectionForm.items[itemIndex].subItems.push(this.createEmptySubItem());
  }

  removeFormSubItem(itemIndex: number, subIndex: number): void {
    this.sectionForm.items[itemIndex].subItems.splice(subIndex, 1);
  }

  get isMainMenuSectionFormVisible(): boolean {
    return this.isAddSectionModalOpen && this.currentMenuType === 'main';
  }

  get isEditingSection(): boolean {
    return this.editingSectionIndex !== null;
  }

  get sectionModalTitle(): string {
    return this.isEditingSection ? 'Edit Section' : 'Add New Section';
  }

  async ngOnInit() {
    try {
      const menuSettings = await this.settingsService.getSettingByQuery({ query: { name: 'menu' } });

      if (!menuSettings || !menuSettings.mainMenu || !menuSettings.mainMenu.sections) {
        throw new Error("Menu settings are not structured correctly.");
      }

      this.menuItems = menuSettings.mainMenu.sections.map((section: { items: { [s: string]: unknown; } | ArrayLike<unknown>; }) => ({
        ...section,
        expanded: false,
        items: section.items ? Object.values(section.items).map((item: any) => ({
          ...item,
          expanded: false,
          items: item.items ? item.items.map((subItem: any) => ({
            ...subItem,
            expanded: false
          })) : []
        })) : []
      }));


      this.sideMenuItems = menuSettings.sideMenu?.sections?.flatMap((section: { items: any; }) => section.items || []).map((item: { subMenu: { [s: string]: unknown; } | ArrayLike<unknown>; }) => ({
        ...item,
        expanded: false,
        subMenu: item.subMenu ? Object.values(item.subMenu) : []
      }));

    } catch (error) {
    }
  }

  toggleMenuExpansion(menuItem: any) {
    menuItem.expanded = !menuItem.expanded;
    console.log(`Toggled ${menuItem.label}: ${menuItem.expanded}`);
  }


  async saveMenuOrder(menuType: 'main' | 'side') {
    try {
      const menuSettings = await this.settingsService.getSettingByQuery({
        query: { name: 'menu' }
      });

      if (!menuSettings) {
        throw new Error('Menu settings not found');
      }

      const updatedMenu = { ...menuSettings };

      if (menuType === 'main') {
        updatedMenu.mainMenu = {
            sections: this.menuItems.map(section => ({
                label: section.label,
                items: section.items ? [...section.items] : []
            }))
        };
    } else {
        updatedMenu.sideMenu = {
        sections: [{
            items: this.sideMenuItems.map(item => ({
              label: item.label,
              type: item.type,
              icon: item.icon,
              route: item.route,
              action: item.action,
              permission: item.permission,
              subMenu: item.type === 'collapsible' ? item.subMenu?.map((subItem: { label: any; route: any; action: any; permission: any; }) => ({
                label: subItem.label,
                route: subItem.route,
                action: subItem.action,
                permission: subItem.permission
            })) : []
            }))
          }]
        };
      }

      console.log('Saving menu structure:', updatedMenu);
      const response = await this.settingsService.updateSetting(menuSettings.id, updatedMenu);
      console.log(`${menuType} menu order saved successfully:`, response);

      this.messageService.add({
        severity: 'success',
        summary: 'Menu Order Saved',
        detail: `The ${menuType} menu order has been updated successfully.`,
        life: this.toasterLife
      });

    } catch (error: any) {
      console.error(`Error saving ${menuType} menu order:`, error);
      this.messageService.add({
        severity: 'error',
        summary: 'Save Error',
        detail: error.message || `Failed to save ${menuType} menu order.`,
        life: this.toasterLife
      });
    }
  }


  closeAddSectionModal() {
    this.isAddSectionModalOpen = false;
    this.newSectionName = "";
    this.sectionForm = this.createEmptySectionForm();
    this.editingSectionIndex = null;
  }

  openAddSectionModal(menuType: 'main' | 'side') {
    this.currentMenuType = menuType;
    this.editingSectionIndex = null;
    this.sectionForm = this.createEmptySectionForm();
    this.isAddSectionModalOpen = true;
  }

  // ── Side Menu Item Form ────────────────────────────────────────────────────

  private createEmptySideMenuItemForm(): DynamicEntitiesComponent['sideMenuItemForm'] {
    return { label: '', type: 'single', icon: '', route: '', action: '', permission: '', visible: true, subMenu: [] };
  }

  private createEmptySubMenuItem(): DynamicEntitiesComponent['sideMenuItemForm']['subMenu'][0] {
    return { label: '', route: '', action: '', permission: '' };
  }


  private sideMenuItemFormToItem(): any {
    const form = this.sideMenuItemForm;
    if (form.type === 'collapsible') {
      return {
        label: form.label.trim(),
        type: form.type,
        permission: form.permission.trim() || null,
        visible: form.visible,
        expanded: false,
        subMenu: form.subMenu.map(sub => ({
          label: sub.label.trim(),
          route: sub.route.trim() || null,
          action: sub.action.trim() || null,
          permission: sub.permission.trim() || null,
        }))
      };
    }
    return {
      label: form.label.trim(),
      type: form.type,
      icon: form.icon.trim() || null,
      route: form.route.trim() || null,
      action: form.action.trim() || null,
      permission: form.permission.trim() || null,
      visible: form.visible,
    };
  }

  private loadSideMenuItemForm(item: any): void {
    const subMenu = Array.isArray(item.subMenu) ? item.subMenu : (item.subMenu ? Object.values(item.subMenu) : []);
    this.sideMenuItemForm = {
      label: item.label ?? '',
      type: item.type === 'collapsible' ? 'collapsible' : 'single',
      icon: item.icon ?? '',
      route: item.route ?? '',
      action: item.action ?? '',
      permission: item.permission ?? '',
      visible: item.visible === true || item.visible === false ? item.visible : true,
      subMenu: subMenu.map((sub: any) => ({
        label: sub.label ?? '',
        route: sub.route ?? '',
        action: sub.action ?? '',
        permission: sub.permission ?? '',
      }))
    };
  }

  private validateSideMenuItemForm(): string | null {
    if (!this.sideMenuItemForm.label?.trim()) return 'Error: The label field is required.';
    if (!this.sideMenuItemForm.type) return 'Error: The type field is required.';
    if (this.sideMenuItemForm.type === 'single' &&
        !this.sideMenuItemForm.route?.trim() &&
        !this.sideMenuItemForm.action?.trim()) {
      return 'Error: A single item must have either a route or an action defined.';
    }
    if (this.sideMenuItemForm.type === 'collapsible') {
      for (let i = 0; i < this.sideMenuItemForm.subMenu.length; i++) {
        if (!this.sideMenuItemForm.subMenu[i].label?.trim()) {
          return `Error: Sub-item ${i + 1} must have a label defined.`;
        }
      }
    }
    return null;
  }

  addSubMenuItem(): void {
    this.sideMenuItemForm.subMenu.push(this.createEmptySubMenuItem());
  }

  removeSubMenuItem(index: number): void {
    this.sideMenuItemForm.subMenu.splice(index, 1);
  }

  removeMainMenuItem(index: number): void {
    this.confirmationService.confirm({
      message: `Remove "${this.menuItems[index].label}" from the main menu?`,
      header: 'Remove Section',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.menuItems.splice(index, 1);
        this.saveUpdatedMenu();
      }
    });
  }

  removeSideMenuItem(index: number): void {
    this.confirmationService.confirm({
      message: `Remove "${this.sideMenuItems[index].label}" from the side menu?`,
      header: 'Remove Item',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.sideMenuItems.splice(index, 1);
        this.saveUpdatedMenu();
      }
    });
  }

  openAddSideMenuItemModal(): void {
    this.sideMenuItemForm = this.createEmptySideMenuItemForm();
    this.editingSideMenuItemIndex = null;
    this.isAddSideMenuItemModalOpen = true;
  }

  openEditSideMenuItemModal(item: any): void {
    this.editingSideMenuItemIndex = this.sideMenuItems.indexOf(item);
    this.loadSideMenuItemForm(item);
    this.isAddSideMenuItemModalOpen = true;
  }

  closeSideMenuItemModal(): void {
    this.isAddSideMenuItemModalOpen = false;
    this.sideMenuItemForm = this.createEmptySideMenuItemForm();
    this.editingSideMenuItemIndex = null;
  }

  saveSideMenuItemForm(): void {
    const err = this.validateSideMenuItemForm();
    if (err) {
      this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: err, life: this.toasterLife });
      return;
    }
    const item = this.sideMenuItemFormToItem();
    if (this.editingSideMenuItemIndex !== null) {
      this.sideMenuItems[this.editingSideMenuItemIndex] = item;
    } else {
      this.sideMenuItems.push(item);
    }
    this.saveUpdatedMenu();
    this.closeSideMenuItemModal();
  }

  openEditSectionModal(sectionIndex: number) {
    this.currentMenuType = 'main';
    this.editingSectionIndex = sectionIndex;
    this.loadFormFromSection(this.menuItems[sectionIndex]);
    this.isAddSectionModalOpen = true;
  }

  private validateSectionForm(): string | null {
    if (!this.sectionForm.label?.trim()) {
      return 'Section name is required.';
    }
    for (let i = 0; i < this.sectionForm.items.length; i++) {
      const item = this.sectionForm.items[i];
      if (!item.label?.trim()) return `Item ${i + 1}: label is required.`;
      for (let j = 0; j < item.subItems.length; j++) {
        const sub = item.subItems[j];
        if (!sub.label?.trim()) return `Item ${i + 1}, Sub-item ${j + 1}: label is required.`;
        if (!sub.url?.trim()) return `Item ${i + 1}, Sub-item ${j + 1}: URL is required.`;
      }
    }
    return null;
  }

  saveSectionForm() {
    const err = this.validateSectionForm();
    if (err) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: err,
        life: this.toasterLife
      });
      return;
    }
    const section = this.sectionFormToSection();
    if (this.editingSectionIndex !== null) {
      this.menuItems[this.editingSectionIndex] = { ...section, expanded: false };
    } else {
      this.menuItems.push({ ...section, expanded: false });
    }
    this.saveUpdatedMenu();
    this.closeAddSectionModal();
  }





  async saveUpdatedMenu() {
    try {
      const menuSettings = await this.settingsService.getSettingByQuery({ query: { name: 'menu' } });

      if (!menuSettings) {
        throw new Error("Menu settings not found.");
      }

      const updatedMenu = {
        ...menuSettings,
        mainMenu: {
          sections: this.menuItems
        },
        sideMenu: {
          sections: [{ items: this.sideMenuItems }]
        }
      };

      delete updatedMenu._id;  

      await this.settingsService.updateSetting(menuSettings.id, updatedMenu);

      this.messageService.add({
        severity: "success",
        summary: "Menu Updated",
        detail: "The new section has been saved successfully.",
        life: this.toasterLife
      });

      await this.menuService.fetchMenuItems();
      await this.menuService.fetchSideMenuItems();
      
    } catch (error: any) {
      this.messageService.add({
        severity: "error",
        summary: "Update Error",
        detail: error.message || "Failed to update the menu.",
        life: this.toasterLife
      });
    }
  }

  showMainMenuItemJson(index: number) {
    this.editingIndex = index;
    this.selectedItemJson = { ...this.menuItems[index] };
    this.editableJson = JSON.stringify(this.selectedItemJson, null, 2);
    this.selectedItemLabel = `Edit JSON for "${this.menuItems[index].label}"`;
    this.showJsonDialog = true;
}


  onReorder(event: any) {
    if (event.items && Array.isArray(event.items)) {
      this.sideMenuItems = [...event.items];
      console.log('Updated sideMenuItems after reorder:', this.sideMenuItems);
    } else {
      console.error('Invalid event structure:', event);
    }
  }

  initializeExpandedState() {
    this.sideMenuItems = this.sideMenuItems.map(item => ({
      ...item,
      expanded: false
    }));
  }

  copyJsonToClipboard() {
    const jsonStr = JSON.stringify(this.selectedItemJson, null, 2);
    navigator.clipboard.writeText(jsonStr).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'JSON copied to clipboard',
        life: this.toasterLife
      });
    });
  }




  
  
  saveJsonChanges() {
    try {
      if (this.editingIndex === null) {
        throw new Error("No item selected for editing.");
      }
  
      const updatedJson = JSON.parse(this.editableJson);
  
      if (!updatedJson || !updatedJson.label) {
        throw new Error("Invalid JSON format. Please ensure the structure is correct.");
      }
  
      this.menuItems[this.editingIndex] = updatedJson;
  
      this.settingsService.updateSetting(1, { mainMenu: { sections: this.menuItems } })
        .then(() => {
          this.messageService.add({
            severity: "success",
            summary: "Update Successful",
            detail: "The JSON has been updated successfully.",
            life: this.toasterLife,
          });
          this.showJsonDialog = false;
          this.editingIndex = null; 
        })
        .catch(error => {
          throw new Error(error.message || "Failed to update the JSON.");
        });
    } catch (error: any) {
      console.error("Error updating JSON:", error);
      this.messageService.add({
        severity: "error",
        summary: "Update Error",
        detail: error.message || "Failed to update the JSON. Please check the format.",
        life: this.toasterLife,
      });
    }
  }


  
  
  async onReorderSideMenu(items: any[]) {
    console.log("Reorder side menu event triggered:", items);
  
    if (Array.isArray(items) && items.length > 0) {
      this.sideMenuItems = [...items];
  
      console.log("Updated sideMenuItems after reorder:", this.sideMenuItems);
  
      try {
        await this.settingsService.updateSetting(1, { sideMenu: { sections: [{ items: this.sideMenuItems }] } });
        this.messageService.add({
          severity: "success",
          summary: "Menu Order Saved",
          detail: "Side menu order updated successfully.",
          life: this.toasterLife
        });
      } catch (error) {
        console.error("Error saving side menu order:", error);
      }
    }
  }
  
  
  

  async onReorderMainMenu(items: any[]) {
    console.log('Reorder main menu event triggered:', items);
    if (Array.isArray(items) && items.length > 0) {
        this.menuItems = items.map(section => ({
            ...section,
            items: section.items ? [...section.items] : [], 
            expanded: section.expanded || false
        }));
    }
}

    
}

